import { useGetUserData } from "./useGetUserData"
import { Datasets } from "../constants/enums"

/**
 * Hook for giving permissions to users based on their role for the LCT
 * and TTR webpahes
 *
 * V2G assessors are a narrow special case: they only get comment powers on
 * a V2G bundle's EVCP (i.e. an EVAC device whose `linkedInverterRef` is set).
 * Callers pass `isV2gDevice` to opt that branch in. They never get alert
 * powers, never get TTR powers, and never get publish powers.
 *
 * @returns {object} - object with boolean values for each action that can be performed
 */
export const useGetLctTtrRbac = (
    deviceRef: string,
    dataset?: Datasets,
    isV2gDevice = false
): {
    canPublishDevice: boolean
    canReadAlerts: boolean
    canRaiseAlerts: boolean
    canDeleteAlert: (username: string | null) => boolean
    canReadComments: boolean
    canCreateComments: boolean
    canPinComments: boolean
} => {
    const isTTRDataset =
        !dataset || dataset === Datasets.TTR || dataset === Datasets.TTR_DRAFT

    const {
        isEnaSysAdmin,
        isManuAdmin,
        isDnoAdmin,
        isDemandAssessor,
        isGenAssessor,
        isV2gAssessor,
        isManuApproved,
        isDnoApproved,
        username,
        isManufacturerForDevice,
    } = useGetUserData()

    const isAssessor = isTTRDataset ? isGenAssessor : isDemandAssessor

    // V2G assessor only gets comment powers on an EVAC device that is part of
    // a V2G bundle (linkedInverterRef set on the device header).
    const isV2gAssessorOnV2gEvac =
        isV2gAssessor && isV2gDevice && dataset === Datasets.EVAC

    const canReadAlerts =
        isEnaSysAdmin ||
        ((isManuAdmin || isManuApproved) &&
            isManufacturerForDevice(deviceRef)) ||
        isDnoApproved ||
        isDnoAdmin ||
        isAssessor ||
        isDnoApproved

    const canRaiseAlerts =
        isEnaSysAdmin ||
        (isManuAdmin && isManufacturerForDevice(deviceRef)) ||
        isDnoAdmin

    const canDeleteAlert = (alerterUsername: string | null): boolean =>
        isEnaSysAdmin ||
        (isDnoAdmin && !!alerterUsername && username === alerterUsername)

    const canPublishDevice = isEnaSysAdmin || isManuApproved

    const canReadComments =
        isEnaSysAdmin ||
        ((isManuAdmin || isManuApproved) &&
            isManufacturerForDevice(deviceRef)) ||
        isDnoAdmin ||
        isDnoApproved ||
        isAssessor ||
        isV2gAssessorOnV2gEvac

    const canCreateComments =
        isEnaSysAdmin ||
        ((isManuAdmin || isManuApproved) &&
            isManufacturerForDevice(deviceRef)) ||
        isDnoAdmin ||
        isDnoApproved ||
        isAssessor ||
        isV2gAssessorOnV2gEvac

    const canPinComments = isAssessor || isV2gAssessorOnV2gEvac || isEnaSysAdmin

    return {
        canPublishDevice,
        canReadAlerts,
        canRaiseAlerts,
        canDeleteAlert,
        canReadComments,
        canCreateComments,
        canPinComments,
    }
}
